/**
 * Geometry for the distribution sketches: density curves with shaded regions, bar
 * charts of a probability mass function, and a line chart for power curves.
 *
 * Pure functions returning SVG path strings and pixel positions, so the drawing is
 * deterministic (it renders identically on the server and in the browser) and testable
 * without a DOM. The widgets turn the result into `<svg>` markup and supply every label.
 */

export interface Frame {
  width: number;
  height: number;
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/**
 * The default frame: a wide, short strip. Axis labels are not drawn inside it — the
 * widgets set them as HTML beside the SVG, positioned from the tick fractions below, so
 * they stay legible at phone width instead of shrinking with the drawing.
 */
export const DENSITY_FRAME: Frame = {
  width: 640,
  height: 210,
  left: 16,
  right: 16,
  top: 12,
  bottom: 8,
};

/** The power curve: the same idea, a little taller. */
export const LINE_FRAME: Frame = {
  width: 640,
  height: 260,
  left: 8,
  right: 16,
  top: 10,
  bottom: 8,
};

export interface Tick {
  /** Pixel position along the axis. */
  position: number;
  value: number;
}

/** A 1, 2 or 5 × 10ᵏ step giving roughly `target` intervals across `span`. */
export function niceStep(span: number, target: number): number {
  const raw = span / Math.max(1, target);
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const residual = raw / magnitude;
  if (residual > 5) {
    return 10 * magnitude;
  }
  if (residual > 2) {
    return 5 * magnitude;
  }
  if (residual > 1) {
    return 2 * magnitude;
  }
  return magnitude;
}

/** Evenly spaced round values inside [min, max]. */
export function niceTicks(min: number, max: number, target = 6): number[] {
  if (!(max > min && Number.isFinite(min) && Number.isFinite(max))) {
    return [min];
  }
  const step = niceStep(max - min, target);
  const decimals = Math.max(0, -Math.floor(Math.log10(step)) + 1);
  const first = Math.ceil(min / step - 1e-9);
  const last = Math.floor(max / step + 1e-9);
  const ticks: number[] = [];
  for (let i = first; i <= last; i++) {
    const value = Number((i * step).toFixed(decimals));
    ticks.push(value === 0 ? 0 : value);
  }
  return ticks;
}

function plotWidth(frame: Frame): number {
  return frame.width - frame.left - frame.right;
}

function plotHeight(frame: Frame): number {
  return frame.height - frame.top - frame.bottom;
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}

/* ------------------------------------------------------------------ densities */

export interface DensitySpec {
  pdf: (x: number) => number;
  xMin: number;
  xMax: number;
  /** Intervals to shade; ends beyond the plotted range are clamped to it. */
  shaded: readonly (readonly [number, number])[];
  /** Vertical reference lines, e.g. the observed statistic or interval bounds. */
  markers?: readonly number[];
  samples?: number;
  frame?: Frame;
}

export interface Marker {
  position: number;
  value: number;
  /** The value lies beyond the plotted range and is drawn at that edge. */
  offScale: boolean;
}

export interface DensityGeometry {
  frame: Frame;
  curve: string;
  areas: string[];
  baseline: number;
  ticks: Tick[];
  markers: Marker[];
}

/**
 * The curve's ceiling. Densities that are infinite at zero (χ² and F with one numerator
 * df) would flatten everything else, so the first 2% of the range is ignored when
 * looking for the peak and the spike runs off the top of the frame instead.
 */
function densityCeiling(
  xs: number[],
  ys: number[],
  xMin: number,
  xMax: number
) {
  const skip = xMin + 0.02 * (xMax - xMin);
  let peak = 0;
  for (let i = 0; i < xs.length; i++) {
    if (xs[i] >= skip && Number.isFinite(ys[i]) && ys[i] > peak) {
      peak = ys[i];
    }
  }
  return peak > 0 ? peak * 1.08 : 1;
}

export function densityGeometry(spec: DensitySpec): DensityGeometry {
  const frame = spec.frame ?? DENSITY_FRAME;
  const { xMin, xMax } = spec;
  const samples = spec.samples ?? 240;
  const span = xMax - xMin;
  const xs: number[] = [];
  const ys: number[] = [];
  for (let i = 0; i <= samples; i++) {
    const x = xMin + (span * i) / samples;
    xs.push(x);
    ys.push(spec.pdf(x));
  }
  const ceiling = densityCeiling(xs, ys, xMin, xMax);
  const baseline = frame.height - frame.bottom;
  const px = (x: number) =>
    round(frame.left + ((x - xMin) / span) * plotWidth(frame));
  const py = (y: number) =>
    round(baseline - (Math.min(y, ceiling) / ceiling) * plotHeight(frame));

  const parts: string[] = [];
  for (let i = 0; i < xs.length; i++) {
    const y = ys[i];
    if (!Number.isFinite(y) || y > ceiling) {
      continue;
    }
    if (parts.length === 0) {
      // Enter from the top edge when the curve starts above the frame.
      const start = i > 0 ? frame.top : py(y);
      parts.push(`M${px(xs[i])},${start}`);
    }
    parts.push(`L${px(xs[i])},${py(y)}`);
  }

  const areas = spec.shaded
    .map(([from, to]) => [Math.max(from, xMin), Math.min(to, xMax)] as const)
    .filter(([from, to]) => to > from)
    .map(([from, to]) => {
      const steps = Math.max(8, Math.ceil(((to - from) / span) * samples));
      const points: string[] = [`M${px(from)},${baseline}`];
      for (let i = 0; i <= steps; i++) {
        const x = from + ((to - from) * i) / steps;
        const y = spec.pdf(x);
        points.push(`L${px(x)},${Number.isFinite(y) ? py(y) : frame.top}`);
      }
      points.push(`L${px(to)},${baseline}Z`);
      return points.join("");
    });

  const ticks = niceTicks(xMin, xMax, 7).map((value) => ({
    position: px(value),
    value,
  }));

  const markers = (spec.markers ?? []).map((value) => {
    const clamped = Math.min(xMax, Math.max(xMin, value));
    return { position: px(clamped), value, offScale: clamped !== value };
  });

  return {
    frame,
    curve: parts.join(""),
    areas,
    baseline,
    ticks,
    markers,
  };
}

/* ------------------------------------------------------------------- bars */

export interface Bar {
  k: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface BarGeometry {
  frame: Frame;
  bars: Bar[];
  baseline: number;
  ticks: Tick[];
}

/** One bar per value of k, in order. `points` must be sorted by k and contiguous. */
export function barGeometry(
  points: readonly { k: number; p: number }[],
  frame: Frame = DENSITY_FRAME
): BarGeometry {
  const baseline = frame.height - frame.bottom;
  if (points.length === 0) {
    return { frame, bars: [], baseline, ticks: [] };
  }
  const kMin = points[0].k;
  const kMax = points.at(-1)?.k ?? kMin;
  const slot = plotWidth(frame) / (kMax - kMin + 1);
  let peak = 0;
  for (const point of points) {
    peak = Math.max(peak, point.p);
  }
  const ceiling = peak > 0 ? peak * 1.08 : 1;
  const barWidth = Math.max(1, slot * 0.78);
  const bars = points.map(({ k, p }) => {
    const height = (p / ceiling) * plotHeight(frame);
    return {
      k,
      x: round(frame.left + (k - kMin) * slot + (slot - barWidth) / 2),
      y: round(baseline - height),
      width: round(barWidth),
      height: round(height),
    };
  });
  const ticks = niceTicks(kMin, kMax, 8)
    .filter((value) => Number.isInteger(value))
    .map((value) => ({
      position: round(frame.left + (value - kMin) * slot + slot / 2),
      value,
    }));
  return { frame, bars, baseline, ticks };
}

/* ------------------------------------------------------------------- lines */

export interface LineGeometry {
  frame: Frame;
  path: string;
  xTicks: Tick[];
  yTicks: Tick[];
  baseline: number;
  project: (x: number, y: number) => { x: number; y: number };
}

export function lineGeometry(
  points: readonly { x: number; y: number }[],
  bounds: { xMin: number; xMax: number; yMin: number; yMax: number },
  frame: Frame = LINE_FRAME
): LineGeometry {
  const { xMin, xMax, yMin, yMax } = bounds;
  const baseline = frame.height - frame.bottom;
  const project = (x: number, y: number) => ({
    x: round(frame.left + ((x - xMin) / (xMax - xMin)) * plotWidth(frame)),
    y: round(baseline - ((y - yMin) / (yMax - yMin)) * plotHeight(frame)),
  });
  const path = points
    .map((point, index) => {
      const { x, y } = project(point.x, point.y);
      return `${index === 0 ? "M" : "L"}${x},${y}`;
    })
    .join("");
  const xTicks = niceTicks(xMin, xMax, 7).map((value) => ({
    position: project(value, yMin).x,
    value,
  }));
  const yTicks = niceTicks(yMin, yMax, 5).map((value) => ({
    position: project(xMin, value).y,
    value,
  }));
  return { frame, path, xTicks, yTicks, baseline, project };
}
